import { Lock, FileText, ArrowRight } from "lucide-react";
import { BANNER } from "@/constants/testIds";
import { useVerificationDialog } from "@/contexts/VerificationDialogContext";

export default function PrivateAccessBanner() {
  const { openDialog } = useVerificationDialog();

  return (
    <section
      data-testid={BANNER.section}
      className="relative bg-[#0F172A] text-white overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid-dark opacity-90 pointer-events-none" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(800px 400px at 90% 10%, rgba(180,83,9,0.18), transparent 60%)," +
            "radial-gradient(700px 400px at 10% 90%, rgba(5,150,105,0.18), transparent 60%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 border border-white/15 bg-white/5 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] font-bold text-slate-200">
            <Lock size={13} className="text-emerald-400" />
            Client Engagement — By Invitation
          </div>

          <h2 className="font-display mt-7 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-[1.1]">
            Confidential sourcing engagements with{" "}
            <span className="font-semibold">US corporate buyers</span>.
          </h2>

          <p className="mt-6 text-slate-300 max-w-2xl text-[17px] leading-relaxed">
            Supplier shortlists, negotiation notes, and procurement updates are
            shared exclusively through secured client channels. Engagements
            begin only after we agree on sourcing scope, confidentiality, and
            commercial fit.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <button
              type="button"
              onClick={openDialog}
              data-testid={BANNER.ctaRegisterClinic}
              className="group inline-flex items-center justify-center gap-2 bg-white text-slate-900 hover:bg-slate-100 px-7 py-4 text-[15px] font-semibold tracking-tight rounded-sm transition-all"
            >
              <FileText size={18} />
              Tell Us What You Need Sourced
              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-1"
              />
            </button>
            <a
              href="#compliance"
              className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white text-white px-7 py-4 text-[15px] font-semibold tracking-tight rounded-sm transition-colors"
            >
              Read Engagement Policy
            </a>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="bg-white/[0.04] backdrop-blur-sm border border-white/10 p-8">
            <div className="eyebrow text-slate-300">Engagement Standards</div>
            <ul className="mt-6 space-y-5">
              {[
                {
                  k: "NDA-First",
                  v: "Mutual non-disclosure executed before any sourcing brief is shared.",
                },
                {
                  k: "Defined Scope",
                  v: "Every sourcing engagement documented in a Statement of Work prior to kickoff.",
                },
                {
                  k: "Local Liaison",
                  v: "On-the-ground team in the Dominican Republic for direct supplier interface.",
                },
                {
                  k: "Audit-Ready Logs",
                  v: "Every supplier interaction and negotiation step timestamped for client review.",
                },
              ].map((row) => (
                <li
                  key={row.k}
                  className="grid grid-cols-[160px_1fr] gap-4 items-start"
                >
                  <div className="text-[12px] uppercase tracking-[0.18em] font-semibold text-emerald-300">
                    {row.k}
                  </div>
                  <div className="text-sm text-slate-200 leading-relaxed">
                    {row.v}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
